/**
 * Replay strategy registry — single source of truth for the
 * "Adjust Last Operation" panel.
 *
 * Every adjustable op declares a `replayKind` in its `COMMAND_METADATA`
 * entry. The matching strategy in this file owns:
 *
 *   - the `capture` helper that the producing site invokes at confirm time
 *     to build the strategy-specific payload stored on `LastOperation.capture`;
 *   - the `replay` callback that `adjustLastOperation` invokes when a slider
 *     in the redo panel changes a parameter.
 *
 * Adding a new adjustable op category — operator, selection tool, or future
 * variant — means defining a new `ReplayKind`, adding a `LastOperationCapture`
 * union variant in `storeTypes.ts`, and registering one strategy here. No
 * branching at call sites.
 */

import {
  deriveVerticesFromFaces,
  selectSimilarFacesByNormal,
} from "../../modeling/editSelection.ts";
import {
  recomputeLoopNormals as meshRecomputeLoopNormals,
  resolveAutoSmoothAngle,
} from "../../modeling/meshOps.ts";
import type { EditModeState } from "../../modeling/types.ts";
import { unwrapBoxProject } from "../../modeling/uvUnwrap.ts";
import { patchSceneObject } from "../sceneObjectMap.ts";
import { reconcileArrayBlock } from "../storeGeneratorOps.ts";
import type {
  AppState,
  CommandBuffer,
  CommandSnapshot,
  GeometrySnapshotCapture,
  LastOperation,
  ReplayKind,
  SelectionSeedCapture,
} from "../storeTypes.ts";
import {
  applyEditObjectSnapshot,
  applySnapshot,
  captureSnapshot,
  commandSnapshotObject,
  isEditObjectSnapshot,
} from "../storeUndo.ts";
import {
  notifyTopologyOpFailure,
  type TopologyOpFailureId,
} from "../topologyOpNotifications.ts";
import type { Get_, Set_ } from "./core.ts";
import { executeTopologyOp } from "./topologyExecute.ts";

// ---------------------------------------------------------------------------
// Strategy interface
// ---------------------------------------------------------------------------

/**
 * Uniform consumption interface used by `adjustLastOperation`. Each strategy
 * narrows `lastOp.capture` to its own variant via the `kind` discriminator.
 */
export interface ReplayStrategy {
  kind: ReplayKind;
  replay(
    set: Set_,
    get: Get_,
    lastOp: LastOperation,
    newParams: Record<string, number | boolean | string>,
  ): void;
}

// ---------------------------------------------------------------------------
// Geometry-snapshot strategy — used by extrude / bevel / inset / loop cut /
// every modal topology op. Restores the pre-op geometry snapshot then re-runs
// `executeTopologyOp` with the fresh parameter set.
// ---------------------------------------------------------------------------

/**
 * Build a `GeometrySnapshotCapture` from the command buffer + pre-op snapshot.
 * Returns `null` when the snapshot is missing — the caller should treat the
 * op as non-replayable in that case (matches the prior `commandPreSnapshot`
 * guard inside `commandConfirm`).
 */
export function captureGeometrySnapshot(
  cb: CommandBuffer,
  commandPreSnapshot: CommandSnapshot | null,
  editMode: EditModeState | null,
): GeometrySnapshotCapture | null {
  if (!commandPreSnapshot) return null;
  return {
    kind: "geometry-snapshot",
    snapshot: commandPreSnapshot,
    objectId: editMode?.objectId ?? null,
    preEditFaces: cb.preEditFaces,
    preEditSelection: cb.preEditSelection,
    preEditEdges: cb.preEditEdges,
    preEditActiveVertex: cb.preEditActiveVertex,
    preEditActiveEdge: cb.preEditActiveEdge,
    preEditActiveFace: cb.preEditActiveFace,
    edgeLoopInsertEdge: cb.edgeLoopInsertEdge,
  };
}

function topologyFailureIdForCommand(
  type: LastOperation["type"],
): TopologyOpFailureId | null {
  switch (type) {
    case "extrude":
      return "extrude";
    case "subdivide":
      return "subdivide";
    case "inset":
      return "inset";
    case "bevel":
      return "bevel";
    case "bridgeEdgeLoops":
      return "bridge_edge_loops";
    case "gridFill":
      return "grid_fill";
    case "mergeByDistance":
      return "merge_by_distance";
    case "edgeLoopInsert":
      return "edge_loop_insert";
    default:
      return null;
  }
}

/**
 * Restore the snapshot-captured geometry, recompute normals + UVs against the
 * fresh result. Extracted from the post-`executeTopologyOp` block so the
 * strategy owns the full replay round-trip.
 */
function rebuildShadingAfterTopologyReplay(
  set: Set_,
  get: Get_,
  objectId: string,
): void {
  const current = get();
  const updatedObj = current.sceneObjects.get(objectId);
  if (!updatedObj || !current.editMode || current.editMode.isCurve) return;
  const loopNormals = meshRecomputeLoopNormals(
    updatedObj.vertices,
    updatedObj.topology,
    resolveAutoSmoothAngle(updatedObj.shading, updatedObj.autoSmoothAngle),
  );
  const uvs = unwrapBoxProject(
    updatedObj.vertices,
    loopNormals,
    updatedObj.topology,
  );
  set({
    sceneObjects: patchSceneObject(current.sceneObjects, objectId, {
      loopNormals,
      uvs,
    }),
  });
}

const geometrySnapshotStrategy: ReplayStrategy = {
  kind: "geometry-snapshot",
  replay(set, get, lastOp, newParams) {
    if (lastOp.capture.kind !== "geometry-snapshot") return;
    const capture = lastOp.capture;
    const state = get();
    if (!state.editMode || capture.objectId == null) return;

    const beforeAdjustSnapshot = captureSnapshot(state);

    // Restore pre-op geometry from snapshot
    const restorePatch: Partial<AppState> = isEditObjectSnapshot(
      capture.snapshot,
    )
      ? applyEditObjectSnapshot(state, capture.snapshot)
      : applySnapshot(state, capture.snapshot);
    set(restorePatch);

    const restored = get();
    const obj = restored.sceneObjects.get(capture.objectId);
    if (!obj || !restored.editMode) return;

    const snapObj = commandSnapshotObject(capture.snapshot, capture.objectId);
    if (!snapObj) return;

    const srcVerts = new Float32Array(snapObj.vertices);

    const applied = executeTopologyOp(
      lastOp.type,
      newParams,
      srcVerts,
      snapObj,
      restored.editMode,
      obj,
      restored,
      set,
      {
        preEditFaces: capture.preEditFaces,
        preEditSelection: capture.preEditSelection,
        preEditEdges: capture.preEditEdges,
        preEditActiveVertex: capture.preEditActiveVertex,
        preEditActiveEdge: capture.preEditActiveEdge,
        preEditActiveFace: capture.preEditActiveFace,
        edgeLoopInsertEdge: capture.edgeLoopInsertEdge,
      },
    );

    if (applied.ok) {
      rebuildShadingAfterTopologyReplay(set, get, capture.objectId);
      // Update stored params (don't push undo — the existing entry covers
      // pre-op state).
      set({ lastOperation: { ...lastOp, params: { ...newParams } } });
    } else {
      const revertPatch = applySnapshot(get(), beforeAdjustSnapshot);
      const failureId = topologyFailureIdForCommand(lastOp.type);
      set({ ...revertPatch, lastOperation: lastOp });
      if (failureId) notifyTopologyOpFailure(failureId, applied.reason);
    }
  },
};

// ---------------------------------------------------------------------------
// Selection-seed strategy — used by selection-only ops (Select Similar by
// Normal, future Select Similar by Material/Area/etc.). Replays the pure
// selection function against the frozen seed; never touches geometry.
// ---------------------------------------------------------------------------

/**
 * Snapshot the current edit-mode face selection + hidden-face mask as a
 * selection-seed capture. Returns `null` when the editMode is missing or the
 * caller passes an empty seed (selection ops are no-ops without a seed).
 */
export function captureSelectionSeed(
  em: EditModeState,
): SelectionSeedCapture | null {
  if (em.selectedFaces.size === 0) return null;
  return {
    kind: "selection-seed",
    objectId: em.objectId,
    seedFaces: new Set(em.selectedFaces),
    hiddenFacesAtSeedTime: new Set(em.hiddenFaces ?? []),
  };
}

const selectionSeedStrategy: ReplayStrategy = {
  kind: "selection-seed",
  replay(set, get, lastOp, newParams) {
    if (lastOp.capture.kind !== "selection-seed") return;
    const capture = lastOp.capture;
    const state = get();
    if (!state.editMode || state.editMode.objectId !== capture.objectId) {
      // The user switched objects since confirm — the seed is meaningless
      // here. Mirror geometry-snapshot's "snapshot exists but the world has
      // moved on" behavior by clearing the redo-panel state entirely.
      set({ lastOperation: null });
      return;
    }
    const obj = state.sceneObjects.get(capture.objectId);
    if (!obj?.topology) return;

    const threshold = Number(newParams.threshold ?? 0);
    const next = selectSimilarFacesByNormal(
      obj.vertices,
      obj.topology,
      capture.seedFaces,
      capture.hiddenFacesAtSeedTime,
      threshold,
    );
    const selectedVertices = deriveVerticesFromFaces(obj.topology, next);

    set({
      editMode: {
        ...state.editMode,
        selectedFaces: next,
        selectedVertices,
        selectedEdges: new Set(),
      },
      lastOperation: { ...lastOp, params: { ...newParams } },
    });
  },
};

// ---------------------------------------------------------------------------
// Object-create strategy — generic object-mode redo hook. It currently owns
// only Count + Offset driven Array reconciliation; surface creation tools use
// persistent generators instead of this transient replay path.
// ---------------------------------------------------------------------------

const objectCreateStrategy: ReplayStrategy = {
  kind: "object-create",
  replay(set, get, lastOp, newParams) {
    if (lastOp.capture.kind !== "object-create") return;
    const capture = lastOp.capture;
    const { objectId } = capture;
    const state = get();
    if (!state.sceneObjects.has(objectId)) {
      // Object was deleted between confirm and adjust — clear the panel.
      set({ lastOperation: null });
      return;
    }
    const nextOverrides = reconcileArrayBlock(
      state.modifierOverrides,
      objectId,
      newParams,
    );
    set({
      modifierOverrides: nextOverrides,
      lastOperation: { ...lastOp, params: { ...newParams } },
    });
  },
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const REPLAY_STRATEGIES: Record<ReplayKind, ReplayStrategy> = {
  "geometry-snapshot": geometrySnapshotStrategy,
  "selection-seed": selectionSeedStrategy,
  "object-create": objectCreateStrategy,
};
